import { useEffect, useMemo, useState } from "react";
import {
  copyTableIntoDocument, linkTableIntoDocument, type InsertMode, type InsertResult, type PlaceBlock,
} from "./tableLinking";
import type { TableSummary } from "./useTables";
import { searchTables } from "./tableSearch";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;
export interface DocSummary {
  id: string;
  title: string;
  updatedAt?: string;
  lastOpenedAt?: string;
  sectionCount?: number;
}

/**
 * Putting a table into a paper, without the markup: what the dialog lists, what
 * is chosen, and what Link and Copy do. Kept apart from `TableInsertDialog` so
 * the dialog's design can be replaced without touching the behaviour.
 *
 *   pickTable    from a document: which of my tables goes in here?
 *   pickDocument from a table:    which paper does this go in?
 */
export function useTableInsert({
  mode, fetchWithAuth, tables, table, documentId, place, onDone,
}: {
  mode: "pickTable" | "pickDocument";
  fetchWithAuth: Fetch;
  tables?: TableSummary[];
  table?: TableSummary;
  documentId?: string;
  place?: PlaceBlock;
  onDone: (r: InsertResult) => void;
}) {
  const [query, setQuery] = useState("");
  const [docs, setDocs] = useState<DocSummary[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "pickDocument") return;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/documents?page=1&pageSize=100");
        const body = (await res.json()) as { items?: DocSummary[] };
        setDocs(body.items ?? []);
      } catch {
        setDocs([]);
      }
    })();
  }, [mode, fetchWithAuth]);

  // Tables are found by name, label, column or cell (tableSearch); papers by title.
  const hits = useMemo(
    () => (mode === "pickTable" ? searchTables(tables ?? [], query) : []),
    [mode, tables, query],
  );
  const rows: (TableSummary | DocSummary)[] = useMemo(() => {
    if (mode === "pickTable") return hits.map((h) => h.table);
    const q = query.trim().toLowerCase();
    const all = docs ?? [];
    return q ? all.filter((d) => (d.title || "untitled").toLowerCase().includes(q)) : all;
  }, [mode, hits, docs, query]);
  const matches = useMemo(() => new Map(hits.map((h) => [h.table.id, h.match])), [hits]);

  const selected = mode === "pickTable"
    ? (tables ?? []).find((t) => t.id === chosen)
    : table;

  /**
   * Put the chosen table into the chosen paper. `place` overrides where it
   * lands (the sheet's landing slot); `stay` leaves the flow open for the next
   * paper instead of finishing it. Resolves to what went in, or null.
   */
  const run = async (
    how: InsertMode,
    opts: { place?: PlaceBlock; stay?: boolean; table?: TableSummary } = {},
  ): Promise<InsertResult | null> => {
    const t = opts.table ?? (mode === "pickTable" ? (tables ?? []).find((x) => x.id === chosen) : table);
    const doc = mode === "pickTable" ? documentId : chosen ?? undefined;
    if (!t || !doc) return null;

    setBusy(true);
    setError(null);
    const where = opts.place ?? place;
    const r = how === "link"
      ? await linkTableIntoDocument(fetchWithAuth, t, doc, where)
      : await copyTableIntoDocument(fetchWithAuth, t, doc, where);
    setBusy(false);
    if (!r) {
      setError("That did not go in. Nothing was changed.");
      return null;
    }
    if (!opts.stay) onDone(r);
    return r;
  };

  return {
    query, setQuery, rows, chosen, setChosen, busy, error, run, selected, matches,
    /** pickDocument: null while the papers are loading. */
    docs,
    /** How many papers the selected table is in already — the Link button's consequence. */
    count: selected?.documentCount ?? 0,
  };
}
